import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Save, Send, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';

const PROVIDER_PRESETS = {
  'Google Workspace (Gmail)': { smtp_host: 'smtp.gmail.com', smtp_port: 587, use_tls: true, hint: 'Generate an App Password from your Google Account → Security → 2-Step Verification → App passwords.' },
  'Zoho Mail': { smtp_host: 'smtp.zoho.in', smtp_port: 465, use_tls: false, hint: 'Use your Zoho Mail password or generate an App Password in Zoho Account → Security.' },
  'Microsoft 365 / Outlook': { smtp_host: 'smtp.office365.com', smtp_port: 587, use_tls: true, hint: 'Ensure SMTP AUTH is enabled for the mailbox in M365 admin.' },
  'Custom SMTP': null,
};

export default function EmailSettings() {
  const navigate = useNavigate();
  const [provider, setProvider] = useState('Google Workspace (Gmail)');
  const [cfg, setCfg] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'ELR Industries Pvt Ltd',
    use_tls: true,
    company_send_domain: 'elloraepc.com',
    allow_dynamic_from: true,
  });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/settings/email');
      if (res.configured) {
        setConfigured(true);
        setCfg(prev => ({
          ...prev,
          smtp_host: res.smtp_host || prev.smtp_host,
          smtp_port: res.smtp_port || prev.smtp_port,
          smtp_user: res.smtp_user || '',
          from_email: res.from_email || '',
          from_name: res.from_name || prev.from_name,
          use_tls: res.use_tls ?? true,
          company_send_domain: res.company_send_domain || prev.company_send_domain,
          allow_dynamic_from: res.allow_dynamic_from ?? true,
          smtp_password: '', // never sent back
        }));
      }
    } catch (err) { /* not configured yet */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (name) => {
    setProvider(name);
    const p = PROVIDER_PRESETS[name];
    if (p) setCfg(prev => ({ ...prev, ...p }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!payload.smtp_password) delete payload.smtp_password; // keep existing
      await apiPut('/settings/email', payload);
      toast.success('Email settings saved');
      setCfg(prev => ({ ...prev, smtp_password: '' }));
      setConfigured(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error('Enter a recipient'); return; }
    setTesting(true);
    try {
      await apiPost('/settings/email/test', { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
    setTesting(false);
  };

  const preset = PROVIDER_PRESETS[provider];

  return (
    <div className="p-6 space-y-6" data-testid="email-settings-page">
      <button onClick={() => navigate('/app/settings')} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-[#1F7A6E]">
        <ArrowLeft size={14} /> Back to Settings
      </button>

      <PageHeader title="Email Configuration (SMTP)" subtitle="Send transactional emails (letters, payslips, notifications) from your own @elloraepc.com mailbox — no external service required." icon={Mail} />

      {configured && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-center gap-2 text-sm text-emerald-800">
          <ShieldCheck size={16} /> Email is currently <b>configured</b>. From: {cfg.from_email || cfg.smtp_user}
        </div>
      )}

      {/* per-use-case SMTP overrides live on a sister page */}
      <div className="bg-teal-50/40 border border-teal-200 rounded-sm p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-white border border-teal-200 flex items-center justify-center shrink-0">
          <Mail size={16} className="text-[#1F7A6E]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-display font-bold text-[#0F172A]">From-Addresses &amp; Per-Use-Case SMTP</div>
          <p className="text-xs text-slate-600 mt-0.5">Route HR mails from HR, POs from Procurement, payslips from Payroll. Each use-case can also override SMTP credentials so it sends from a dedicated mailbox.</p>
        </div>
        <a href="/app/settings/email-from-map" className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-[10px] font-display font-bold uppercase tracking-wider rounded-sm whitespace-nowrap"
          data-testid="email-from-map-link">
          Open →
        </a>
      </div>

      {/* daily-digest troubleshooting */}
      <div className="bg-amber-50/40 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-white border border-amber-200 flex items-center justify-center shrink-0">
          <AlertCircle size={16} className="text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-display font-bold text-[#0F172A]">Daily Email Diagnostics</div>
          <p className="text-xs text-slate-600 mt-0.5">Someone not getting the daily digests? See the exact recipient list, look up a person to check their email + opt-in status, re-enable them, or send a test — without spamming everyone.</p>
        </div>
        <a href="/app/settings/email-diagnostics" className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-display font-bold uppercase tracking-wider rounded-sm whitespace-nowrap"
          data-testid="email-diagnostics-link">
          Open →
        </a>
      </div>

      {!configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-2 text-sm text-amber-800">
          <AlertCircle size={16} /> Email is not yet configured. Letters and payslip emailing will fail until you save.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5 max-w-3xl">
          <div>
            <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-2">Provider Preset</label>
            <select value={provider} onChange={e => applyPreset(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="provider-preset">
              {Object.keys(PROVIDER_PRESETS).map(p => <option key={p}>{p}</option>)}
            </select>
            {preset?.hint && <div className="text-xs text-slate-500 mt-1.5">{preset.hint}</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="SMTP Host *" value={cfg.smtp_host} onChange={v => setCfg(c => ({ ...c, smtp_host: v }))} testid="smtp-host" placeholder="smtp.gmail.com" />
            <Field label="SMTP Port *" value={cfg.smtp_port} onChange={v => setCfg(c => ({ ...c, smtp_port: parseInt(v) || 587 }))} testid="smtp-port" type="number" />
            <Field label="SMTP Username *" value={cfg.smtp_user} onChange={v => setCfg(c => ({ ...c, smtp_user: v }))} testid="smtp-user" placeholder="hr@elloraepc.com" />
            <Field label={configured ? 'SMTP Password (leave blank to keep)' : 'SMTP Password / App Password *'} value={cfg.smtp_password} onChange={v => setCfg(c => ({ ...c, smtp_password: v }))} testid="smtp-password" type="password" />
            <Field label="From Email *" value={cfg.from_email} onChange={v => setCfg(c => ({ ...c, from_email: v }))} testid="from-email" placeholder="hr@elloraepc.com" />
            <Field label="From Name" value={cfg.from_name} onChange={v => setCfg(c => ({ ...c, from_name: v }))} testid="from-name" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.use_tls} onChange={e => setCfg(c => ({ ...c, use_tls: e.target.checked }))} className="w-4 h-4 accent-[#1F7A6E]" data-testid="use-tls" />
            <span className="text-sm">Use STARTTLS (recommended for port 587)</span>
          </label>
          {cfg.smtp_port === 587 && !cfg.use_tls && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Port 587 requires STARTTLS. Gmail and most providers will reject login without it — enable the checkbox above.
            </p>
          )}

          {/* Mixed-domain From (Fork 53) */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 mb-2">
              Mixed-Domain Senders
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Emails can be <b>SENT TO</b> any address (Gmail, contractor, client). The <b>From</b> address can only be a company-domain mailbox; for users without a company email, mails show their name with replies routed to their personal address.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Company Send Domain"
                value={cfg.company_send_domain}
                onChange={v => setCfg(c => ({ ...c, company_send_domain: v }))}
                testid="company-send-domain"
                placeholder="elloraepc.com"
              />
              <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
                <input
                  type="checkbox"
                  checked={!!cfg.allow_dynamic_from}
                  onChange={e => setCfg(c => ({ ...c, allow_dynamic_from: e.target.checked }))}
                  className="w-4 h-4 accent-[#1F7A6E]"
                  data-testid="allow-dynamic-from"
                />
                <span className="text-sm">Allow dynamic <b>From</b> for company-domain users</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="save-email-settings">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>

          {configured && (
            <div className="border-t border-slate-200 pt-5">
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 mb-2">Send a Test Email</div>
              <div className="flex gap-2">
                <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="your.email@elloraepc.com" className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="test-recipient" />
                <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="send-test">
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Test
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text', placeholder }) {
  return (
    <div>
      <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E]" data-testid={testid} />
    </div>
  );
}
